<script setup>
  import { onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n';
  import ClassificationTreeNode from './ClassificationTreeNode.vue';

  const { t } = useI18n();

  const props = defineProps({
    tree: {
      type: Array,
      required: true,
      default: () => []
    },
    selected: {
      type: Array,
      required: true,
      default: () => []
    },
    url_key: {
      type: String,
      required: true
    },
    apply_url: {
      type: String,
      required: true
    }
  });

  const selectedCodes = ref(props.selected);

  // Same contract as the other multiselect facets: the url_key parameter holds
  // the selected codes joined by a comma, and applying resets the pagination.
  const apply = () => {
    const codes = selectedCodes.value;

    if (codes.length > 0) {
      window.posthog?.capture(`catalog-facet-${props.url_key}-${codes.join('-')}`);
    }

    const url = new URL(props.apply_url, window.location.href);
    url.searchParams.delete(props.url_key);
    if (codes.length > 0) {
      url.searchParams.append(props.url_key, codes.join(','));
    }

    window.location.href = url.toString();
  };

  const list = ref(null);
  const canScrollUp = ref(false);
  const canScrollDown = ref(false);
  let contentObserver = null;

  // Arrows hint at the content hidden above and below the visible part of the
  // list. A one pixel tolerance keeps them from flickering on fractional
  // scroll positions.
  const updateScrollHints = () => {
    const el = list.value;
    if (!el) return;

    const scrollable = el.scrollHeight - el.clientHeight;
    canScrollUp.value = el.scrollTop > 1;
    canScrollDown.value = el.scrollTop < scrollable - 1;
  };

  onMounted(() => {
    updateScrollHints();
    // Expanding or collapsing a node changes the scrollable height.
    contentObserver = new MutationObserver(updateScrollHints);
    contentObserver.observe(list.value, { childList: true, subtree: true });
    window.addEventListener('resize', updateScrollHints);
    canScrollDown.value = true; // Show the down arrow until the first updateScrollHints() call.
  });

  onUnmounted(() => {
    contentObserver?.disconnect();
    window.removeEventListener('resize', updateScrollHints);
  });
</script>

<template>
  <div class="classification-tree__scroller">
    <ul
      ref="list"
      class="list-unstyled ps-0 mb-0 classification-tree__list"
      @scroll="updateScrollHints"
    >
      <ClassificationTreeNode
        v-for="node in tree"
        :key="node.code"
        v-model:selected-codes="selectedCodes"
        :node="node"
        :url-key="url_key"
      />
    </ul>
    <span v-show="canScrollUp" class="classification-tree__hint classification-tree__hint--up" aria-hidden="true">
      <i class="bi bi-arrow-up-circle-fill"></i>
    </span>
    <span v-show="canScrollDown" class="classification-tree__hint classification-tree__hint--down" aria-hidden="true">
      <i class="bi bi-arrow-down-circle-fill"></i>
    </span>
  </div>
  <div class="row mt-3">
    <div class="col">
      <button
        :id="`${url_key}_tree_facet__apply`"
        class="btn btn-outline-secondary btn-sm ms-0 classification-tree__apply"
        type="button"
        @click="apply"
      >
        {{ t('components.classification_tree.apply') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .classification-tree__scroller {
    position: relative;
  }

  // Full trees run to thousands of codes: cap the list and scroll it so the
  // apply button stays reachable.
  .classification-tree__list {
    max-height: 700px;
    overflow-y: auto;
  }

  // Overlays the scrolled content, hence the fade behind the arrow and the
  // pointer events left to the checkboxes underneath.
  .classification-tree__hint {
    position: absolute;
    right: 0;
    left: 0;
    display: flex;
    justify-content: center;
    height: 1.25rem;
    color: var(--bs-secondary-color, #6c757d);
    font-size: 1.25rem;
    line-height: 1;
    pointer-events: none;

    &--up {
      top: 0;
      align-items: flex-start;
      background: linear-gradient(to bottom, var(--bs-body-bg, #fff), rgba(255, 255, 255, 0));
    }

    &--down {
      bottom: 0;
      align-items: flex-end;
      background: linear-gradient(to top, var(--bs-body-bg, #fff), rgba(255, 255, 255, 0));
    }
  }
</style>
